import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ArrowUp } from "lucide-react";
import api from "@/lib/api";

type LiveUpdate = { id: string; at: string; label?: string | null; body: string };

const ACTIVE_STATES = ["breaking", "developing", "live"];
const INITIAL_SHOWN = 4;

const fmtTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return ""; }
};
const fmtDay = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return ""; }
};

/* Live Updates timeline (§approved plan 3). Polls /articles/{slug}/live every
   60s while the story is live (hook-isolated — swappable for SSE later).
   New updates never jerk the reader's scroll: if the timeline is off-screen,
   a subtle "new update" control appears instead. */
export const LiveTimeline = ({ slug, article }: { slug: string; article: any }) => {
  const initialActive = ACTIVE_STATES.includes(article.live_state) ||
    (!article.live_state && !!article.is_breaking && (article.live_updates || []).length > 0);
  const hasInitial = (article.live_updates || []).length > 0;

  const [shown, setShown] = useState<LiveUpdate[]>(article.live_updates || []);
  const [pending, setPending] = useState<LiveUpdate[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  const visible = useRef(true);

  const { data } = useQuery({
    queryKey: ["article-live", slug],
    queryFn: async () => (await api.get(`/articles/${slug}/live`)).data,
    enabled: hasInitial || initialActive,
    refetchInterval: (q) => {
      const st = (q.state.data as any)?.live_state;
      const act = st ? ACTIVE_STATES.includes(st) : initialActive;
      return act ? 60 * 1000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const liveState = data?.live_state ?? article.live_state;
  const active = liveState
    ? ACTIVE_STATES.includes(liveState)
    : initialActive;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { visible.current = e.isIntersecting; }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const fresh: LiveUpdate[] = data?.updates;
    if (!fresh) return;
    const known = new Set(shown.map((u) => u.id));
    const isNew = fresh.some((u) => !known.has(u.id));
    if (!isNew) {
      if (fresh.length !== shown.length) setShown(fresh);
      return;
    }
    if (visible.current || window.scrollY < 300) setShown(fresh);
    else setPending(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const mergePending = () => {
    if (pending) { setShown(pending); setPending(null); }
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const newCount = pending ? pending.filter((u) => !shown.some((s) => s.id === u.id)).length : 0;

  if (!shown.length && !active) return null;

  const list = expanded ? shown : shown.slice(0, INITIAL_SHOWN);
  const hiddenCount = shown.length - list.length;

  return (
    <>
      {newCount > 0 && (
        <button type="button" onClick={mergePending} data-testid="live-new-update-pill"
          className="fixed top-20 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-1.5 rounded-full border border-[#EF4444]/40 bg-[#1a0d0d]/95 backdrop-blur px-3.5 py-1.5 font-mono text-[10.5px] font-bold uppercase tracking-wider text-[#FCA5A5] shadow-lg hover:text-white transition-colors">
          <ArrowUp className="h-3 w-3" /> {newCount} new update{newCount > 1 ? "s" : ""}
        </button>
      )}
      <section ref={ref} data-testid="live-timeline" aria-label="Live updates"
        className={`mt-6 rounded-xl border p-4 sm:p-5 ${
          active ? "border-[#EF4444]/25 bg-[#120c0e]" : "border-white/[0.08] bg-[#0a0b10]"}`}>
        <div className="flex items-center justify-between gap-3">
          <p className={`flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.28em] ${
            active ? "text-[#FCA5A5]" : "text-gray-500"}`}>
            {active && (
              <span className="relative inline-flex h-2 w-2" aria-hidden>
                <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EF4444]" />
              </span>
            )}
            {active ? "Live Updates" : "Update Log"}
          </p>
          {!active && shown.length > 0 && (
            <span className="font-mono text-[9px] uppercase tracking-wider text-gray-600" data-testid="live-concluded-note">
              Coverage concluded
            </span>
          )}
        </div>

        {list.length === 0 ? (
          <p className="mt-3 text-[13px] text-gray-400" data-testid="live-awaiting-note">
            Live coverage is underway — updates will appear here.
          </p>
        ) : (
          <ol className="mt-4 space-y-4">
            {list.map((u, i) => (
              <li key={u.id} data-testid={`live-update-${u.id}`} className="relative pl-5">
                <span aria-hidden className={`absolute left-0 top-[7px] h-2 w-2 rounded-full ${
                  i === 0 && active ? "bg-[#EF4444]" : "bg-white/25"}`} />
                {(i < list.length - 1 || hiddenCount > 0) && (
                  <span aria-hidden className="absolute left-[3.5px] top-[18px] bottom-[-14px] w-px bg-white/[0.08]" />
                )}
                <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <time dateTime={u.at} className="font-mono text-[10.5px] font-bold text-gray-400">
                    {fmtTime(u.at)} <span className="text-gray-600 font-normal">{fmtDay(u.at)}</span>
                  </time>
                  {i === 0 && active && (
                    <span data-testid="live-latest-badge"
                      className="font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-[#EF4444] border border-[#EF4444]/40 rounded px-1.5 py-[1px]">
                      Latest
                    </span>
                  )}
                  {u.label && <span className="text-[13px] font-bold text-white">{u.label}</span>}
                </p>
                <p className="mt-1 text-[13.5px] text-gray-300 leading-relaxed">{u.body}</p>
              </li>
            ))}
          </ol>
        )}

        {hiddenCount > 0 && (
          <button type="button" onClick={() => setExpanded(true)} data-testid="live-view-earlier"
            className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10.5px] font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-colors">
            <ChevronDown className="h-3 w-3" /> View earlier updates ({hiddenCount})
          </button>
        )}
      </section>
    </>
  );
};
